import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IngestionArtifactModel, IngestionArtifactType, IngestionResourceModel } from '../../../../../../gamma-api';
import IbSpin from '../../../../../components/common/IbSpin';
import IbTypography from '../../../../../components/common/IbTypography';
import ResizableDrawer from '../../../../../components/common/ResizableDrawer';
import IbIcon from '../../../../../components/common/IbIcon';
import IbContextMenu from '../../../../../components/common/IbContextMenu';
import IbButton from '../../../../../components/common/IbButton';

import './index.less';
import { MAIN_CLASS_NAME, TITLE_CLASS_NAME } from './const';

export interface IDocumentListProps {
  visible: boolean;
  loading: boolean;
  resource?: IngestionResourceModel;
  items: IngestionArtifactModel[];
  onOpen?: (
    resource: IngestionResourceModel,
    artifactType: IngestionArtifactType,
    oldArtifactType?: IngestionArtifactType
  ) => void;
  onClose?: (artifactType: IngestionArtifactType) => void;
}

const DocumentList: React.FC<IDocumentListProps> = ({ visible, loading, resource, items, onOpen, onClose }) => {
  const { t } = useTranslation();

  const [menuVisible, setMenuVisible] = useState(false);

  const renderTitle = () => {
    const identity = resource?.identities.latest;
    const parent = identity?.title || identity?.name;
    const current = t('Document list: Title');
    const title = parent ? `${parent} / ${current}` : current;
    const reference = resource?.references.latest;
    const url = reference?.artifact_urls[0]?.public || reference?.file_urls[0]?.public;

    const onButtonClick = () => {
      setMenuVisible(true);
    };

    const onMenuClose = () => {
      setMenuVisible(false);
    };

    const onDownloadSelect = () => {
      // eslint-disable-next-line security/detect-non-literal-fs-filename
      url && window.open(url, '_blank');
    };

    const onArticlesSelect = () => {
      resource && onOpen?.(resource, IngestionArtifactType.Article, IngestionArtifactType.Document);
    };

    const onFragmentsSelect = () => {
      resource && onOpen?.(resource, IngestionArtifactType.Fragment, IngestionArtifactType.Document);
    };

    const menuItems = [
      {
        icon: <IbIcon iconName="download" size={20} />,
        text: t('Resource table: Context: Download'),
        onSelect: onDownloadSelect,
        disabled: !url,
      },
      {
        icon: <IbIcon iconName="file-text" size={20} />,
        text: t('Resource table: Context: Open articles'),
        onSelect: onArticlesSelect,
        disabled: !resource,
      },
      {
        icon: <IbIcon iconName="view-list" size={20} />,
        text: t('Resource table: Context: Open fragments'),
        onSelect: onFragmentsSelect,
        disabled: !resource,
      },
    ];

    return (
      <div className={TITLE_CLASS_NAME}>
        <span>{title}</span>
        <IbContextMenu menuItems={menuItems} visible={menuVisible} onClose={onMenuClose}>
          <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onButtonClick} />
        </IbContextMenu>
      </div>
    );
  };

  const renderItem = (item: IngestionArtifactModel) => {
    return (
      <IbTypography.Paragraph type="secondary">
        <pre>{item.content.data}</pre>
      </IbTypography.Paragraph>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <IbSpin />;
    }

    return items.map(renderItem);
  };

  const onDrawerClose = () => {
    onClose?.(IngestionArtifactType.Document);
  };

  return (
    <ResizableDrawer
      resizable
      className={MAIN_CLASS_NAME}
      title={renderTitle()}
      visible={visible}
      width="75%"
      onClose={onDrawerClose}
    >
      {renderContent()}
    </ResizableDrawer>
  );
};

export default DocumentList;
